// University of Kentucky: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UK's Common Data Set 2025-2026 (Institutional Research, Analytics, and
//    Decision Support; the newest edition on its Common Data Set page),
//    read from the rendered PDF pages because parts of its text layer are
//    garbled: C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table (the Unknown column is blank and left out), C8 test
//    policy for Fall 2027, C21/C22 (no early decision plan; a nonbinding
//    early action plan closing December 1, with no counts). Its C8E gives
//    May 1 as the latest date for scores; UK's Test Optional FAQ
//    (admission.uky.edu/news/test-optional) says scores can be sent until
//    the December 1 or February 15 deadline, so the page gives no score date.
//  - admission.uky.edu: Freshman Admission ("Apply Now for Fall 2027", the
//    two deadlines; test-optional "through the 2028-29 academic year", so
//    nothing changes for fall 2028 entry), Admission Checklist (the Fall 2027
//    deadlines table, test scores), Selective Majors and Programs.
//  - Office of Student Financial Aid and Scholarships, Estimated Cost of
//    Attendance 2026-2027: undergraduate on-campus budgets, in-state and
//    out-of-state. Departure: the CDS G1/G5 splits tuition ($12,714
//    in-state, $34,383 out-of-state) from required fees ($1,463), which sum
//    to $14,177 in-state, $1 under the aid office's $14,178 tuition and fees;
//    the CDS also gives no loan fees and one transportation figure. The aid
//    office's budget is used because it is the one UK builds aid offers on,
//    and its items sum to its own totals.
//  - College Scorecard API, school id 157085 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UK Common Data Set 2025-2026',
        url: 'https://irads.uky.edu/common-data-set',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UK Office of Undergraduate Admission, Freshman Admission',
        url: 'https://admission.uky.edu/freshman',
        read: '2026-10-01',
    },
    checklist: {
        label: 'UK Office of Undergraduate Admission, Admission Checklist',
        url: 'https://admission.uky.edu/freshman/admission-checklist',
        read: '2026-10-01',
    },
    selective: {
        label: 'UK Office of Undergraduate Admission, Selective Majors and Programs',
        url: 'https://admission.uky.edu/selective-majors-and-programs',
        read: '2026-10-01',
    },
    coa: {
        label: 'UK Student Financial Aid and Scholarships, Estimated Cost of Attendance 2026-2027',
        url: 'https://studentsuccess.uky.edu/financial-aid-and-scholarships/estimated-cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?157085-University-of-Kentucky',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-kentucky',
    kbId: 'university_of_kentucky',
    name: 'University of Kentucky',
    shortName: 'UK',
    aliases: ['UK', 'UKY'],
    location: 'Lexington, KY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UK brand color palette); decoration only.
    branding: {
        primary: '#0033A0',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_kentucky_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 33387,
        admitted: 31222,
        enrolled: 6960,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 9087, admitted: 8658 },
            { group: 'Out-of-state', applied: 23214, admitted: 21588 },
            { group: 'International', applied: 1086, admitted: 976 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UK\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UK has no early decision plan. Its Early Action plan, with a December 1 deadline, is nonbinding and not restrictive. The Common Data Set gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most UK majors admit you directly, or as a pre-major, when UK admits you. Some colleges run their own admission process for selective majors:',
        rows: [
            { school: 'Selective majors in Business and Economics, Design, Engineering, Fine Arts, Health Sciences, Public Policy, Nursing, Pharmacy, Public Health and Social Work', requirement: 'Depending on the program: higher GPAs or test scores than general admission (including Kentucky\'s statewide academic readiness levels in reading, writing and math, which can also be met through a review of your high school courses and grades), a separate application after you are admitted to UK, a portfolio, or an on-campus or video interview. If you choose a selective major, UK may place you in another major until the program decides.' },
        ],
        source: 'selective',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 15' },
        rows: [
            { plan: 'Early Action', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'February 15', decision: null, binding: false },
        ],
        note: 'December 1 is also the deadline for competitive and academic scholarships and the Lewis Honors College, for Early Action applicants only. The FAFSA priority deadline is March 1, the enrollment deposit is due May 1, Living Learning Program applications May 15 and the housing and dining application June 1. A deadline on a weekend moves to 11:59 p.m. Eastern the next Monday. UK does not publish decision dates.',
        source: 'checklist',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'ACT or SAT scores are not required for admission, but you may send them, and UK says it is test-optional through the 2028-29 academic year. Scores you send must be official, from the testing agency or on your official high school transcript. UK accepts the ACT composite with or without the optional science section.',
        source: 'checklist',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required but considered if submitted for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [14178, 35846] },
            { item: 'Food and housing (on campus)', amounts: [17048, 17048] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Travel', amounts: [2592, 3418] },
            { item: 'Personal expenses', amounts: [3240, 3240] },
            { item: 'Loan origination fees', amounts: [86, 86] },
        ],
        totals: [38344, 60838],
        note: 'For an undergraduate living on campus without dependents, fall and spring. College of Design students pay an extra $686 program fee each semester.',
        source: 'coa',
        netPrice: {
            average: 18868,
            byIncome: [
                { income: '$0 to $30,000', amount: 12528 },
                { income: '$30,001 to $48,000', amount: 12460 },
                { income: '$48,001 to $75,000', amount: 14192 },
                { income: '$75,001 to $110,000', amount: 20327 },
                { income: 'Over $110,000', amount: 27661 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UK\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
